
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { type Company } from "@/types";
import { toast } from "sonner";

export const useCompanyData = (userId: string | undefined) => {
  const [company, setCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlanAllowed, setIsPlanAllowed] = useState(false);

  const fetchUserCompany = useCallback(async () => {
    if (!userId) return;
    
    try {
      setIsLoading(true);
      console.log("Fetching company data for user:", userId);
      
      // First get the user's profile to get their company_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        return;
      }

      console.log("User profile:", profile);

      if (profile?.company_id) {
        // Fetch the company details using the company_id from the profile
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('*')
          .eq('id', profile.company_id)
          .maybeSingle();

        if (companyError) {
          console.error('Error fetching company:', companyError);
          return;
        }

        console.log("Company data fetched:", companyData);
        
        if (companyData) {
          setCompany(companyData);
          // Check if the plan allows access to premium features
          setIsPlanAllowed(companyData.plan === 'starter' || companyData.plan === 'enterprise');
        }
      } else {
        console.log("No company_id found in user profile");
      }
    } catch (error) {
      console.error('Error in fetchUserCompany:', error);
      toast.error('Failed to load company data');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchUserCompany();
    }
  }, [userId, fetchUserCompany]);

  return { company, isLoading, isPlanAllowed, refetchCompany: fetchUserCompany };
};
